import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/AuthProvider/useAuth";
import { formatDataCompare, getLocalStorage, isMobileDevice, setLocalStorage, toastError } from "../../../../commons/utils";
import { Grid, Box, LinearProgress } from "@mui/material";
import { Input } from "../../../../components/Input";
import { model_fornecedor, model_grupo, model_imobilizado, model_motivo_requisicao, model_usuario } from "../../../../models";
import moment from "moment";
import { ExSelectFilter, ExSelectFilterHandler } from "../../../../components/ExSelectFilter";
import { ExCheckBox } from "../../../../components/ExCheckBox";

const REFRESH_INTERVAL = 60000;

export const FiltroRequisicoes = forwardRef((props: any, ref: any) => {
    const auth = useAuth();

    const refdatai = useRef<any>();
    const refdataf = useRef<any>();
    const refdatavenci = useRef<any>(null);
    const refdatavencf = useRef<any>(null);
    const reffornecedor = useRef<ExSelectFilterHandler>(null);
    const refimobilizado = useRef<ExSelectFilterHandler>(null);
    const refGrupo = useRef<ExSelectFilterHandler>(null);
    const refMotivo = useRef<ExSelectFilterHandler>(null);
    const refcheckboximob = useRef<any>(null);
    const refSolicitante = useRef<ExSelectFilterHandler>(null);

    const [data, setData] = useState<any>();
    const [refreshProgress, setRefreshProgress] = useState<number>(0);
    const [autoRefreshActive, setAutoRefreshActive] = useState(false);

    const refreshTimer = useRef<NodeJS.Timeout | null>(null);
    const progressTimer = useRef<NodeJS.Timeout | null>(null);
    const abaAtiva = useRef(true);


    function clearTimers() {
        if (refreshTimer.current) clearInterval(refreshTimer.current);
        if (progressTimer.current) clearInterval(progressTimer.current);
        setRefreshProgress(0);
    }

    function startAutoRefresh() {
        clearTimers();
        setAutoRefreshActive(true);
        setRefreshProgress(100);

        let remaining = 100;
        const step = 100 / (REFRESH_INTERVAL / 100);

        progressTimer.current = setInterval(() => {
            remaining -= step;
            if (remaining < 0) remaining = 0;
            setRefreshProgress(remaining);
        }, 100);

        refreshTimer.current = setInterval(() => {
            if (abaAtiva.current) {
                change(true);
                remaining = 100;
                setRefreshProgress(100);
            }
        }, REFRESH_INTERVAL);
    }

    function refresh() {
        change(true);
    }

    useImperativeHandle(ref, () => ({
        refresh
    }))

    useEffect(() => {
        function onVisibilityChange() {
            abaAtiva.current = !document.hidden;
        }
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            clearTimers();
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);

    async function getDados(refresh: boolean, iData?: any, fData?: any, fOnlyImobNotCat?: boolean) {
        let _data: any;

        if (!refresh && data)
            _data = data;
        else {
            await auth.get('/v1/requisicaokanban')
                .then((response: any) => {
                    setData(response.data);
                    _data = response.data;
                })
                .catch((error: any) => {
                    toastError(error);
                })
        }

        let _iData = iData ?? refdatai.current?.getValue();
        let _fData = fData ?? refdataf.current?.getValue();
        let _iDataVenc = refdatavenci.current?.getValue();
        let _fDataVenc = refdatavencf.current?.getValue();

        if (!_iData && (_iData !== '') && refdatai.current)
            _iData = refdatai.current.getValue();

        if (!_fData && (_fData !== '') && refdataf.current)
            _fData = refdataf.current.getValue();

        if (_iData && (_iData !== ''))
            _data = _data.filter((item: any) => formatDataCompare(item.reqdata) >= _iData);

        if (_fData && (_fData !== ''))
            _data = _data.filter((item: any) => formatDataCompare(item.reqdata) <= _fData);

        if (_iDataVenc && (_iDataVenc !== ''))
            _data = _data.filter((item: any) => formatDataCompare(item.reqdtvencimento) >= _iDataVenc);

        if (_fDataVenc && (_fDataVenc !== ''))
            _data = _data.filter((item: any) => formatDataCompare(item.reqdtvencimento) <= _fDataVenc);

        if (fOnlyImobNotCat)
            _data = _data.filter((item: any) => item.imobcatid === null);

        _data = refimobilizado.current?.applyFilter(_data) || _data
        _data = reffornecedor.current?.applyFilter(_data) || _data
        _data = refGrupo.current?.applyFilter(_data) || _data
        _data = refMotivo.current?.applyFilter(_data) || _data
        _data = refSolicitante.current?.applyFilter(_data) || _data

        // Armazena os filtros 
        setLocalStorage('iDataKanbanRequisicoes', _iData ?? null);
        setLocalStorage('fDataKanbanRequisicoes', _fData ?? null);

        return _data;
    }

    function change(refresh: boolean, iData?: any, fData?: any, fOnlyImobNotCat?: boolean) {
        getDados(refresh, iData, fData, fOnlyImobNotCat).then((data: any) => {
            if (props.onGetData)
                props.onGetData(data);
            startAutoRefresh();
        });
    }

    function changeDataInicial(_newValue: any) { change(false, _newValue) }
    function changeDataFinal(_newValue: any) { change(false, null, _newValue) }
    function changeImobCat(checked: boolean) { change(false, null, null, checked) }
    function changeFilter() { change(false) }

    useEffect(() => {
        change(true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Grid item xs={12}>
                {autoRefreshActive && (
                    <Box sx={{ width: "100%", mb: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={refreshProgress}
                            sx={{
                                height: 2,
                                "& .MuiLinearProgress-bar": {
                                    backgroundColor: "var(--primary-color)"
                                }
                            }}
                        />
                    </Box>
                )}
                <Grid container spacing={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Grid item xs={isMobileDevice() ? 6 : 1.5}>
                        <Input ref={refdatai} id="iDataKanbanRequisicoes" type="date" label="Data" onAfterChange={changeDataInicial} defaultValue={getLocalStorage('iDataKanbanRequisicoes') ? getLocalStorage('iDataKanbanRequisicoes') : moment().format('YYYY-MM-01')} />
                    </Grid>
                    <Grid item xs={isMobileDevice() ? 6 : 1.5}>
                        <Input ref={refdataf} id="fDataKanbanRequisicoes" type="date" label=" " onAfterChange={changeDataFinal} defaultValue={moment().format('YYYY-MM-DD')} />
                    </Grid>
                    {props.showVencimento && <>
                        <Grid item xs={isMobileDevice() ? 6 : 1.5}>
                            <Input ref={refdatavenci} id="iDataVencKanbanRequisicoes" type="date" label="Vencimento" onAfterChange={changeFilter} />
                        </Grid>
                        <Grid item xs={isMobileDevice() ? 6 : 1.5}>
                            <Input ref={refdatavencf} id="fDataVencKanbanRequisicoes" type="date" label=" " onAfterChange={changeFilter} />
                        </Grid>
                    </>}
                    <Grid item xs={isMobileDevice() ? 6 : 3}>
                        <ExSelectFilter
                            ref={refSolicitante}
                            model={model_usuario}
                            label={'Solicitante'}
                            filterField="reqsolicitante"
                            onAfterChange={changeFilter}
                            variant='kanban'
                        />
                    </Grid>
                    <Grid item xs={isMobileDevice() ? 6 : 2} style={{ marginTop: '24px' }} >
                        <ExCheckBox ref={refcheckboximob} id="checkimob" label="Apenas Imobilizados sem Categoria" onClick={(e: any) => changeImobCat(e.target.checked)} />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Grid item xs={isMobileDevice() ? 12 : 2.85}>
                        <ExSelectFilter ref={reffornecedor} model={model_fornecedor} onAfterChange={changeFilter} variant='kanban' />
                    </Grid>
                    <Grid item xs={isMobileDevice() ? 12 : 2.85}>
                        <ExSelectFilter ref={refimobilizado} model={model_imobilizado} onAfterChange={changeFilter} variant='kanban' />
                    </Grid>
                    <Grid item xs={isMobileDevice() ? 12 : 2.853}>
                        <ExSelectFilter
                            ref={refGrupo}
                            model={model_grupo}
                            onAfterChange={changeFilter}
                            variant="kanban"
                        />
                    </Grid>
                    <Grid item xs={isMobileDevice() ? 12 : 2.85}>
                        <ExSelectFilter
                            ref={refMotivo}
                            model={model_motivo_requisicao}
                            onAfterChange={changeFilter}
                            variant="kanban"
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
})